import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { formatSats } from "@/game/format";

interface StackSatsGameProps {
  onFinish: (sats: number) => void;
}

interface Target {
  id: number;
  x: number;
  y: number;
  value: number;
  bad: boolean;
}

const GAME_DURATION = 15; // seconds

export function StackSatsGame({ onFinish }: StackSatsGameProps) {
  const [running, setRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [score, setScore] = useState(0);
  const [targets, setTargets] = useState<Target[]>([]);
  const idRef = useRef(0);
  const areaRef = useRef<HTMLDivElement>(null);

  // countdown
  useEffect(() => {
    if (!running) return;
    if (timeLeft <= 0) {
      setRunning(false);
      return;
    }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [running, timeLeft]);

  // spawn targets
  useEffect(() => {
    if (!running) return;
    const spawn = setInterval(() => {
      setTargets((prev) => {
        const bad = Math.random() < 0.25;
        const t: Target = {
          id: idRef.current++,
          x: 8 + Math.random() * 84,
          y: 12 + Math.random() * 76,
          value: bad ? -300 : 100 + Math.floor(Math.random() * 5) * 50,
          bad,
        };
        const next = [...prev, t];
        return next.slice(-6);
      });
    }, 650);
    return () => clearInterval(spawn);
  }, [running]);

  // clear targets when game ends
  useEffect(() => {
    if (!running && timeLeft <= 0) {
      setTargets([]);
    }
  }, [running, timeLeft]);

  const handleStart = () => {
    setScore(0);
    setTimeLeft(GAME_DURATION);
    setTargets([]);
    setRunning(true);
  };

  const hit = (t: Target) => {
    setScore((s) => Math.max(0, s + t.value));
    setTargets((prev) => prev.filter((x) => x.id !== t.id));
  };

  const finished = !running && timeLeft <= 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between font-terminal text-2xl">
        <span>⏱️ {timeLeft}s</span>
        <span className="text-primary">⚡ {formatSats(score)} sats</span>
      </div>

      <div
        ref={areaRef}
        className="relative h-72 w-full overflow-hidden rounded-lg border-2 border-primary/40 bg-gradient-to-b from-secondary/40 to-background"
      >
        {!running && !finished && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
            <p className="font-terminal text-xl">
              Tap the ⚡ coins to stack sats. Avoid the 💀 scam coins — they cost
              you!
            </p>
            <Button onClick={handleStart} className="font-pixel text-[10px]">
              Start Mining
            </Button>
          </div>
        )}

        {finished && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
            <p className="font-pixel text-sm text-primary">
              +{formatSats(score)} sats!
            </p>
            <div className="flex gap-2">
              <Button onClick={handleStart} variant="secondary" className="font-pixel text-[10px]">
                Retry
              </Button>
              <Button onClick={() => onFinish(score)} className="font-pixel text-[10px]">
                Bank It
              </Button>
            </div>
          </div>
        )}

        {running &&
          targets.map((t) => (
            <button
              key={t.id}
              onClick={() => hit(t)}
              className="absolute -translate-x-1/2 -translate-y-1/2 text-4xl transition-transform hover:scale-110 active:scale-90"
              style={{ left: `${t.x}%`, top: `${t.y}%` }}
              aria-label={t.bad ? "scam coin" : "sats coin"}
            >
              {t.bad ? "💀" : "⚡"}
            </button>
          ))}
      </div>

      {!finished && running && (
        <p className="text-center font-terminal text-lg text-muted-foreground">
          Stack 'em while you can!
        </p>
      )}
    </div>
  );
}
